/**
 * CDM Privacy — Track 4.17.
 *
 * Two tabs:
 *   1. Field sensitivity matrix — (archetype × field) classification grid.
 *      Click a cell to edit (admin only).
 *   2. GDPR purges — past Article-17 erasures + a destructive "+ New
 *      purge" button gated on typing the target's email + a lawful basis.
 *
 * Backed by /api/cdm/field-sensitivity[/{a}/{f}] and /api/cdm/privacy/*.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Shield, Trash2, Lock, ChevronLeft, RefreshCw, X,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface SensitivityRow {
  archetype: string
  field: string
  level: 'public' | 'restricted' | 'confidential' | 'secret'
  unmask_role: string | null
  unmask_scope: string | null
  description: string | null
  updated_at: string | null
}

interface PurgeRow {
  id: string
  person_id: string
  actor_email: string
  lawful_basis: string
  rows_affected: Record<string, number>
  purged_at: string | null
  notes: string | null
}

const LEVELS: SensitivityRow['level'][] = ['public', 'restricted', 'confidential', 'secret']
const ROLES = ['', 'viewer', 'analyst', 'operator', 'admin']
const SCOPES = ['', 'home_org', 'global_with_audit']
const LAWFUL_BASES = [
  'GDPR_Art17',
  'subject_request',
  'court_order',
  'retention_expiry',
  'regulatory_directive',
]

const LEVEL_BADGE: Record<SensitivityRow['level'], string> = {
  public:       'bg-surface-raised text-text-muted border-border',
  restricted:   'bg-blue-light/15 text-blue-light border-blue-light/40',
  confidential: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  secret:       'bg-red-500/15 text-red-300 border-red-500/40',
}

export default function CdmPrivacyPage() {
  const [tab, setTab] = useState<'matrix' | 'purges'>('matrix')

  // Matrix
  const [rows, setRows] = useState<SensitivityRow[]>([])
  const [loadingRows, setLoadingRows] = useState(false)
  const [editing, setEditing] = useState<SensitivityRow | null>(null)
  const [saving, setSaving] = useState(false)
  const [matrixMessage, setMatrixMessage] = useState('')

  // Purges
  const [purges, setPurges] = useState<PurgeRow[]>([])
  const [loadingPurges, setLoadingPurges] = useState(false)
  const [showNewPurge, setShowNewPurge] = useState(false)
  const [purgeMessage, setPurgeMessage] = useState('')

  const loadRows = () => {
    setLoadingRows(true)
    api.get('/api/cdm/field-sensitivity')
      .then(r => setRows(r.data?.rows || []))
      .catch(() => setRows([]))
      .finally(() => setLoadingRows(false))
  }

  const loadPurges = () => {
    setLoadingPurges(true)
    api.get('/api/cdm/privacy/purge-audit')
      .then(r => setPurges(r.data?.rows || []))
      .catch(() => setPurges([]))
      .finally(() => setLoadingPurges(false))
  }

  useEffect(() => {
    if (tab === 'matrix') loadRows()
    else loadPurges()
  }, [tab])

  // Group matrix rows by archetype for the grid layout
  const grouped = useMemo(() => {
    const m: Record<string, SensitivityRow[]> = {}
    for (const r of rows) {
      if (!m[r.archetype]) m[r.archetype] = []
      m[r.archetype].push(r)
    }
    return m
  }, [rows])

  const saveEdit = async () => {
    if (!editing) return
    setSaving(true)
    setMatrixMessage('')
    try {
      await api.put(
        `/api/cdm/field-sensitivity/${encodeURIComponent(editing.archetype)}/${encodeURIComponent(editing.field)}`,
        {
          level:        editing.level,
          unmask_role:  editing.unmask_role || null,
          unmask_scope: editing.unmask_scope || null,
          description:  editing.description || null,
        },
      )
      setMatrixMessage(`Saved ${editing.archetype}.${editing.field} → ${editing.level}`)
      setEditing(null)
      loadRows()
    } catch (e: any) {
      setMatrixMessage(`Save failed: ${e?.response?.data?.detail || 'unknown'}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Shield size={18} className="text-blue-light" /> CDM PRIVACY
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          field-level sensitivity · GDPR Article-17 erasures
        </span>
      </div>

      {/* Tab strip */}
      <div className="flex items-center gap-2 border-b border-border">
        <button
          type="button"
          onClick={() => setTab('matrix')}
          className={clsx(
            'px-3 py-2 text-xs font-semibold border-b-2 -mb-px inline-flex items-center gap-2',
            tab === 'matrix'
              ? 'border-blue-light text-text-primary'
              : 'border-transparent text-text-muted hover:text-text-secondary',
          )}
        >
          <Lock size={12} /> Field sensitivity
        </button>
        <button
          type="button"
          onClick={() => setTab('purges')}
          className={clsx(
            'px-3 py-2 text-xs font-semibold border-b-2 -mb-px inline-flex items-center gap-2',
            tab === 'purges'
              ? 'border-blue-light text-text-primary'
              : 'border-transparent text-text-muted hover:text-text-secondary',
          )}
        >
          <Trash2 size={12} /> GDPR purges
        </button>
        <div className="ml-auto">
          <button
            type="button"
            onClick={() => (tab === 'matrix' ? loadRows() : loadPurges())}
            disabled={loadingRows || loadingPurges}
            className="btn-ghost"
          >
            <RefreshCw size={11} className={(loadingRows || loadingPurges) ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── Tab: matrix ────────────────────────────────────────────────────── */}
      {tab === 'matrix' && (
        <div className="space-y-4">
          {matrixMessage && (
            <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
              {matrixMessage}
            </div>
          )}

          {Object.keys(grouped).length === 0 && (
            <p className="text-text-muted text-sm">
              {loadingRows ? 'Loading…' : 'No sensitivity classifications yet — bootstrap should have seeded the defaults.'}
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {Object.entries(grouped).map(([archetype, fields]) => (
              <div key={archetype} className="card p-4">
                <h2 className="text-sm font-semibold text-text-primary mb-2 font-mono">
                  {archetype}
                </h2>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-text-muted uppercase tracking-wider">
                      <th className="text-left py-1">Field</th>
                      <th className="text-left py-1">Level</th>
                      <th className="text-left py-1">Unmask role</th>
                      <th className="text-left py-1">Scope</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fields.map(r => (
                      <tr
                        key={`${r.archetype}.${r.field}`}
                        className="hover:bg-surface-raised/40 cursor-pointer border-t border-border"
                        onClick={() => setEditing({ ...r })}
                        title="Edit classification"
                      >
                        <td className="py-1 font-mono text-text-secondary">{r.field}</td>
                        <td className="py-1">
                          <span className={clsx(
                            'inline-block px-2 py-[1px] text-[10px] font-semibold border rounded',
                            LEVEL_BADGE[r.level],
                          )}>
                            {r.level}
                          </span>
                        </td>
                        <td className="py-1 font-mono text-text-muted">{r.unmask_role || '—'}</td>
                        <td className="py-1 font-mono text-text-muted">{r.unmask_scope || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {editing && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
              <div className="card p-5 max-w-lg w-full space-y-3">
                <div className="flex items-center justify-between border-b border-border pb-2">
                  <h2 className="text-sm font-bold text-text-primary font-mono">
                    {editing.archetype}.{editing.field}
                  </h2>
                  <button type="button" onClick={() => setEditing(null)} className="text-text-muted hover:text-text-primary">
                    <X size={16} />
                  </button>
                </div>

                {editing.description && (
                  <p className="text-[11px] text-text-muted italic">{editing.description}</p>
                )}

                <label className="block">
                  <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Level</div>
                  <select
                    value={editing.level}
                    onChange={e => setEditing({ ...editing, level: e.target.value as SensitivityRow['level'] })}
                    className="w-full bg-surface-raised border border-border rounded px-2 py-1 text-xs"
                  >
                    {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </label>

                <label className="block">
                  <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Min unmask role</div>
                  <select
                    value={editing.unmask_role || ''}
                    onChange={e => setEditing({ ...editing, unmask_role: e.target.value || null })}
                    className="w-full bg-surface-raised border border-border rounded px-2 py-1 text-xs"
                  >
                    {ROLES.map(r => <option key={r || '_'} value={r}>{r || '— none —'}</option>)}
                  </select>
                </label>

                <label className="block">
                  <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Unmask scope</div>
                  <select
                    value={editing.unmask_scope || ''}
                    onChange={e => setEditing({ ...editing, unmask_scope: e.target.value || null })}
                    className="w-full bg-surface-raised border border-border rounded px-2 py-1 text-xs"
                  >
                    {SCOPES.map(s => <option key={s || '_'} value={s}>{s || '— none —'}</option>)}
                  </select>
                </label>

                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                  <button type="button" onClick={() => setEditing(null)} className="btn-ghost">Cancel</button>
                  <button type="button" onClick={saveEdit} disabled={saving} className="btn-primary">
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: purges ────────────────────────────────────────────────────── */}
      {tab === 'purges' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowNewPurge(true)}
              className="px-3 py-1.5 text-xs font-semibold rounded border border-red-500/60 bg-red-500/15 text-red-300 hover:bg-red-500/25 inline-flex items-center gap-2"
            >
              <Trash2 size={12} /> New purge
            </button>
            {purgeMessage && (
              <span className="text-[11px] text-text-secondary font-mono">{purgeMessage}</span>
            )}
          </div>

          <div className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary mb-2">Past purges</h2>
            {purges.length === 0 ? (
              <p className="text-text-muted text-sm">
                {loadingPurges ? 'Loading…' : 'No GDPR purges recorded.'}
              </p>
            ) : (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-text-muted uppercase tracking-wider border-b border-border">
                    <th className="text-left py-1">When</th>
                    <th className="text-left py-1">Person ID</th>
                    <th className="text-left py-1">Actor</th>
                    <th className="text-left py-1">Lawful basis</th>
                    <th className="text-left py-1">Rows affected</th>
                    <th className="text-left py-1">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {purges.map(p => (
                    <tr key={p.id} className="border-b border-border/40 hover:bg-surface-raised/40">
                      <td className="py-1 font-mono text-text-secondary">
                        {p.purged_at ? new Date(p.purged_at).toISOString().slice(0, 19).replace('T', ' ') : '—'}
                      </td>
                      <td className="py-1 font-mono text-text-muted">{p.person_id.slice(0, 8)}…</td>
                      <td className="py-1 text-text-secondary">{p.actor_email}</td>
                      <td className="py-1 font-mono text-text-secondary">{p.lawful_basis}</td>
                      <td className="py-1 font-mono text-text-muted">
                        {Object.entries(p.rows_affected || {})
                          .map(([t, c]) => `${t.replace('cdm_', '')}=${c}`)
                          .join(' · ') || '—'}
                      </td>
                      <td className="py-1 text-text-muted truncate max-w-[200px]" title={p.notes || ''}>
                        {p.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {showNewPurge && (
            <NewPurgeDialog
              onClose={() => setShowNewPurge(false)}
              onPurged={(msg) => {
                setShowNewPurge(false)
                setPurgeMessage(msg)
                loadPurges()
              }}
            />
          )}
        </div>
      )}

      <p className="text-[10px] text-text-muted text-center">
        Purge cascades cdm_persons / users / identities / events / role_grants and writes a permanent
        audit row that outlives the purged data. Audit log itself is lawfully retained.
      </p>
    </div>
  )
}


// ── New-purge dialog (separate component to keep state isolated) ────────────

function NewPurgeDialog({
  onClose,
  onPurged,
}: {
  onClose: () => void
  onPurged: (msg: string) => void
}) {
  const [personId, setPersonId] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [lawfulBasis, setLawfulBasis] = useState(LAWFUL_BASES[0])
  const [notes, setNotes] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    setError('')
    if (!personId.trim()) { setError('person_id required'); return }
    if (!confirmEmail.trim()) { setError('Type the person’s email to confirm'); return }
    if (!lawfulBasis) { setError('lawful basis required'); return }
    setBusy(true)
    try {
      const { data } = await api.post('/api/cdm/privacy/purge', {
        person_id:    personId.trim(),
        lawful_basis: lawfulBasis,
        notes:        notes.trim() || `confirmed via email match: ${confirmEmail.trim()}`,
      })
      const counts = Object.entries(data.rows_affected || {})
        .map(([t, c]) => `${t}=${c}`).join(' · ')
      onPurged(`Purged ${data.person_id} — ${counts || 'no rows'}`)
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'purge failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="card p-5 max-w-lg w-full space-y-3 border-2 border-red-500/60">
        <div className="flex items-center justify-between border-b border-red-500/40 pb-2">
          <h2 className="text-sm font-bold text-red-300 font-mono inline-flex items-center gap-2">
            <Trash2 size={14} /> NEW GDPR PURGE
          </h2>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text-primary">
            <X size={16} />
          </button>
        </div>

        <p className="text-[11px] text-text-secondary">
          Cascade-delete all PII for one person. The audit trail (who / when / why) is
          permanently retained but the underlying data is wiped. Irreversible.
        </p>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">person_id (UUID)</div>
          <input
            type="text"
            value={personId}
            onChange={e => setPersonId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="w-full bg-surface-raised border border-border rounded px-2 py-1 text-xs font-mono"
          />
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">
            confirm: type the person’s email
          </div>
          <input
            type="text"
            value={confirmEmail}
            onChange={e => setConfirmEmail(e.target.value)}
            placeholder="alice@example.com"
            className="w-full bg-surface-raised border border-border rounded px-2 py-1 text-xs font-mono"
          />
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Lawful basis</div>
          <select
            value={lawfulBasis}
            onChange={e => setLawfulBasis(e.target.value)}
            className="w-full bg-surface-raised border border-border rounded px-2 py-1 text-xs"
          >
            {LAWFUL_BASES.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
        </label>

        <label className="block">
          <div className="text-[10px] uppercase tracking-widest text-text-muted mb-1">Notes (optional)</div>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            className="w-full bg-surface-raised border border-border rounded px-2 py-1 text-xs"
          />
        </label>

        {error && (
          <div className="text-[11px] text-red-300 font-mono px-2 py-1 border border-red-500/40 rounded bg-red-500/10">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !personId || !confirmEmail}
            className="px-3 py-1.5 text-xs font-semibold rounded border border-red-500/60 bg-red-500/15 text-red-300 hover:bg-red-500/25 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            <Trash2 size={11} /> {busy ? 'Purging…' : 'Purge'}
          </button>
        </div>
      </div>
    </div>
  )
}
